import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Supplier document lines on a task page: the rows Match Supplier Document
 * Lines produced, being reviewed by a person. Search and rematch are
 * read-only. applyTranslation writes one supplier translation
 * (`ap_supplier_products`) after the reviewer confirms. The task correction
 * is still written by the page, not by these procedures.
 */

/** One of the supplier's translations (`ap_supplier_products`). */
export const SupplierTranslationSchema = z.object({
  supplierProductId: z.uuid(),
  productId: z.uuid(),
  productVariantId: z.uuid(),
  supplierSku: z.string().nullable(),
  supplierUpc: z.string().nullable(),
  supplierDescription: z.string().nullable(),
  supplierFreeform: z.array(z.string()),
  productName: z.string(),
  variantSku: z.string().nullable(),
});

export const SearchSupplierTranslationsInputSchema = z.object({
  supplierId: z.uuid(),
  /** Matched against identifiers, description, and freeform terms. Blank lists recent translations. */
  query: z.string().trim().max(200).prefault(""),
  limit: z.number().int().min(1).max(50).prefault(20),
});

export const SearchSupplierTranslationsResponseSchema = z.object({
  translations: z.array(SupplierTranslationSchema),
});

/**
 * A match row as the node emits it. Loose so edited extracted columns and
 * any later keys ride along; the server rewrites only the match keys.
 */
export const SupplierDocumentLineRowSchema = z.looseObject({
  index: z.number().int().nonnegative(),
  extracted: z.record(z.string(), z.unknown()),
  identifiers: z
    .looseObject({
      sku: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
      upc: z.string().nullable().optional(),
      freeform: z.array(z.string()).optional(),
    })
    .optional(),
});

export const RematchSupplierDocumentLinesInputSchema = z.object({
  supplierId: z.uuid(),
  lines: z.array(SupplierDocumentLineRowSchema).max(2000),
});

export const RematchSupplierDocumentLinesResponseSchema = z.object({
  lines: z.array(SupplierDocumentLineRowSchema),
});

const supplierTermSchema = z.string().trim().max(500).nullable();

/**
 * Teach the supplier that this line's terms mean one catalog variant.
 * Freeform must end up non-empty after trimming; the handler rejects a line
 * with no sku, description, upc, or freeform term.
 */
export const ApplySupplierDocumentLineTranslationInputSchema = z.object({
  supplierId: z.uuid(),
  productVariantId: z.uuid(),
  sku: supplierTermSchema,
  description: supplierTermSchema,
  upc: supplierTermSchema,
  freeform: z.array(z.string().trim().max(500)).max(50),
  /** Take these terms off the translation this line had before. */
  removePreviousMatch: z.boolean().prefault(false),
  previousSupplierProductId: z.uuid().nullable(),
});

export const ApplySupplierDocumentLineTranslationResponseSchema = z.object({
  supplierProductId: z.uuid(),
  didCreate: z.boolean(),
  removedPrevious: z.boolean(),
});

export const supplierDocumentLinesContracts = {
  searchTranslations: oc
    .input(SearchSupplierTranslationsInputSchema)
    .output(SearchSupplierTranslationsResponseSchema)
    .route({
      method: "GET",
      path: "/supplier-document-lines/translations",
      summary: "Search a supplier's product translations",
      description:
        "Searches a supplier's saved mappings from their part numbers, UPCs, and descriptions to catalog products. A blank query lists recent ones. Read-only.",
    }),
  rematch: oc
    .input(RematchSupplierDocumentLinesInputSchema)
    .output(RematchSupplierDocumentLinesResponseSchema)
    .route({
      method: "POST",
      path: "/supplier-document-lines/rematch",
      summary: "Rematch supplier document lines",
      description:
        "Re-runs product matching for a supplier's document lines (up to 2000) against their saved translations and returns the lines with updated matches. Saves nothing.",
    }),
  applyTranslation: oc
    .input(ApplySupplierDocumentLineTranslationInputSchema)
    .output(ApplySupplierDocumentLineTranslationResponseSchema)
    .route({
      method: "POST",
      path: "/supplier-document-lines/translations",
      summary: "Save a supplier product translation",
      description:
        "Teaches the system that a document line's SKU, description, UPC, or terms mean one catalog product variant for this supplier, optionally removing the terms from the previous match.",
    }),
} as const;

export type SupplierTranslation = z.infer<typeof SupplierTranslationSchema>;
export type SupplierDocumentLineRow = z.infer<
  typeof SupplierDocumentLineRowSchema
>;
export type RematchSupplierDocumentLinesInput = z.infer<
  typeof RematchSupplierDocumentLinesInputSchema
>;
export type ApplySupplierDocumentLineTranslationInput = z.infer<
  typeof ApplySupplierDocumentLineTranslationInputSchema
>;
export type ApplySupplierDocumentLineTranslationResponse = z.infer<
  typeof ApplySupplierDocumentLineTranslationResponseSchema
>;
